/**
 * ResearchResultRenderer
 *
 * Renders structured research synthesis JSON as a clean, readable UI
 * that adapts its structure to the content — only meaningful sections
 * are shown.  Uses platform-standard Mulish/Poppins typography.
 *
 * Falls back gracefully if keys are missing — renders whatever is present.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiChevronDown,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineBookOpen,
  HiOutlineClipboard,
  HiOutlineCheck,
} from 'react-icons/hi2';

/* ------------------------------------------------------------------ */
/*  Types (local — matches the synthesis JSON schema)                  */
/* ------------------------------------------------------------------ */

interface KeyAuthority {
  citation: string;
  principle: string;
  weight: string;
  application_notes?: string;
}

interface AnalysisSection {
  topic: string;
  content: string;
  key_authorities?: KeyAuthority[];
}

interface AuthorityGroup {
  summary: string;
  citations: string[];
}

interface UncertaintyArea {
  area: string;
  current_status: string;
  relevant_authorities?: string[];
  implications: string;
}

interface SynthesisData {
  original_question?: string;
  executive_summary?: {
    direct_answer: string;
    key_findings: string[];
    confidence_level: string;
    areas_of_uncertainty: string;
  };
  detailed_analysis?: Record<string, AnalysisSection>;
  authority_integration?: {
    binding_authorities?: AuthorityGroup;
    persuasive_authorities?: AuthorityGroup;
  };
  jurisdictional_analysis?: {
    primary_jurisdiction: string;
    applicable_frameworks: string;
    variations_by_jurisdiction?: string;
    federal_vs_state?: string;
  };
  conflicting_authorities?: Array<{ description?: string } | string>;
  areas_of_uncertainty_and_development?: UncertaintyArea[];
  gaps_and_limitations?: {
    information_gaps: string;
    limitation_summary: string;
    recommendations_for_deeper_research: string;
  };
  practical_implications?: Record<string, string>;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Try to parse text as the synthesis JSON. Returns null on failure. */
export function tryParseSynthesis(text: unknown): SynthesisData | null {
  if (!text || typeof text !== 'string') return null;
  if (!text.trimStart().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(text);
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed.executive_summary || parsed.detailed_analysis || parsed.synthesis_date)
    ) {
      return parsed as SynthesisData;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

const confidenceColor: Record<string, string> = {
  high: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  moderate: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  low: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
};

const weightColor: Record<string, string> = {
  binding: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  persuasive: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800',
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

const Badge: React.FC<{ text: string; className?: string }> = ({ text, className }) => (
  <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded border ${className ?? 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700'}`}>
    {text}
  </span>
);

/** Collapsible section with smooth animation */
const Disclosure: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}> = ({ title, children, defaultOpen = false, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const inner = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number | undefined>(defaultOpen ? undefined : 0);

  useEffect(() => {
    if (!inner.current) return;
    if (open) {
      const sh = inner.current.scrollHeight;
      setH(sh);
      const t = setTimeout(() => setH(undefined), 300);
      return () => clearTimeout(t);
    } else {
      const sh = inner.current.scrollHeight;
      setH(sh);
      requestAnimationFrame(() => setH(0));
    }
  }, [open]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!inner.current) return;
    navigator.clipboard.writeText(inner.current.innerText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-gray-150 dark:border-gray-700/60 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
      >
        <HiChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-300 ${open ? 'rotate-0' : '-rotate-90'}`} />
        <span className="flex-1 text-[13px] font-semibold text-gray-800 dark:text-gray-200 font-heading">{title}</span>
        {badge}
        <button
          onClick={handleCopy}
          className="p-1 text-gray-300 hover:text-gray-500 dark:hover:text-gray-400 transition-colors"
          title="Copy"
        >
          {copied ? <HiOutlineCheck className="w-3.5 h-3.5 text-emerald-500" /> : <HiOutlineClipboard className="w-3.5 h-3.5" />}
        </button>
      </button>
      <div
        ref={inner}
        style={{ height: h !== undefined ? `${h}px` : 'auto' }}
        className="transition-[height] duration-300 ease-in-out overflow-hidden"
      >
        <div className="px-4 pb-3 pt-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300 space-y-3">{children}</div>
      </div>
    </div>
  );
};

const CitationChip: React.FC<{ citation: string; weight?: string; onOpenCitation?: (url: string) => void }> = ({
  citation,
  weight,
  onOpenCitation,
}) => (
  <button
    onClick={() => onOpenCitation?.(citation)}
    className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors cursor-pointer"
    title={`View ${citation}`}
  >
    <HiOutlineBookOpen className="w-3 h-3" />
    <span className="max-w-[200px] truncate">{citation}</span>
    {weight && <Badge text={weight} className={weightColor[weight] || ''} />}
  </button>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

interface Props {
  data: SynthesisData;
  onOpenCitation?: (url: string) => void;
}

const ResearchResultRenderer: React.FC<Props> = ({ data, onOpenCitation }) => {
  const { t } = useTranslation();
  const es = data.executive_summary;
  const da = data.detailed_analysis;
  const ai = data.authority_integration;
  const ja = data.jurisdictional_analysis;
  const conflicts = data.conflicting_authorities;
  const uncertainty = data.areas_of_uncertainty_and_development;
  const gaps = data.gaps_and_limitations;
  const pi = data.practical_implications;

  return (
    <div className="research-result space-y-3 animate-fade-in font-sans">
      {/* ── Direct Answer ──────────────────────────────────── */}
      {es && (
        <div className="space-y-2.5">
          {/* Direct answer as flowing prose */}
          {es.direct_answer && (
            <p className="text-[14px] leading-relaxed text-gray-800 dark:text-gray-200">
              {es.direct_answer}
            </p>
          )}

          {/* Key findings as concise bullets */}
          {es.key_findings && es.key_findings.length > 0 && (
            <ul className="space-y-1 ml-0">
              {es.key_findings.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-gray-700 dark:text-gray-300">
                  <HiOutlineCheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Confidence + uncertainty inline */}
          {(es.confidence_level || es.areas_of_uncertainty) && (
            <div className="flex items-start gap-2 flex-wrap">
              {es.confidence_level && (
                <Badge
                  text={`${es.confidence_level.charAt(0).toUpperCase() + es.confidence_level.slice(1)} confidence`}
                  className={confidenceColor[es.confidence_level] || ''}
                />
              )}
              {es.areas_of_uncertainty && (
                <span className="text-[12px] text-gray-500 dark:text-gray-400 flex items-start gap-1">
                  <HiOutlineExclamationTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                  {es.areas_of_uncertainty}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Detailed Analysis ─────────────────────────────── */}
      {da && Object.keys(da).length > 0 && (
        <div className="space-y-2">
          {Object.entries(da).map(([key, section]) => {
            if (!section || typeof section !== 'object') return null;
            const hasAuthorities = section.key_authorities && section.key_authorities.length > 0;
            return (
              <Disclosure
                key={key}
                title={section.topic || key.replace(/_/g, ' ')}
                defaultOpen={true}
              >
                <p className="leading-relaxed">{section.content}</p>

                {hasAuthorities && (
                  <div className="mt-2 space-y-2">
                    {section.key_authorities!.map((auth, i) => (
                      <div key={i} className="pl-3 border-l-2 border-blue-200 dark:border-blue-800 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CitationChip citation={auth.citation} weight={auth.weight} onOpenCitation={onOpenCitation} />
                        </div>
                        {auth.principle && <p className="text-[13px]">{auth.principle}</p>}
                        {auth.application_notes && (
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 italic">{auth.application_notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Disclosure>
            );
          })}
        </div>
      )}

      {/* ── Authority Integration (only if substantive) ───── */}
      {ai && (ai.binding_authorities?.citations?.length || ai.persuasive_authorities?.citations?.length) ? (
        <Disclosure title={t('Authority Integration')}>
          {ai.binding_authorities?.summary && (
            <div className="space-y-1.5">
              <p className="text-[13px]"><span className="font-semibold">Binding:</span> {ai.binding_authorities.summary}</p>
              {ai.binding_authorities.citations.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {ai.binding_authorities.citations.map((c, i) => (
                    <CitationChip key={i} citation={c} weight="binding" onOpenCitation={onOpenCitation} />
                  ))}
                </div>
              )}
            </div>
          )}
          {ai.persuasive_authorities?.summary && ai.persuasive_authorities.citations.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[13px]"><span className="font-semibold">Persuasive:</span> {ai.persuasive_authorities.summary}</p>
              <div className="flex flex-wrap gap-1.5">
                {ai.persuasive_authorities.citations.map((c, i) => (
                  <CitationChip key={i} citation={c} weight="persuasive" onOpenCitation={onOpenCitation} />
                ))}
              </div>
            </div>
          )}
        </Disclosure>
      ) : null}

      {/* ── Jurisdictional Analysis (condensed) ───────────── */}
      {ja && (ja.applicable_frameworks || ja.variations_by_jurisdiction) && (
        <Disclosure title={`${t('Jurisdiction')}: ${ja.primary_jurisdiction || ''}`}>
          {ja.applicable_frameworks && <p className="text-[13px]">{ja.applicable_frameworks}</p>}
          {ja.variations_by_jurisdiction && ja.variations_by_jurisdiction !== 'N/A' && (
            <p className="text-[12px] text-gray-500 dark:text-gray-400">{ja.variations_by_jurisdiction}</p>
          )}
        </Disclosure>
      )}

      {/* ── Conflicting Authorities ───────────────────────── */}
      {conflicts && conflicts.length > 0 && (
        <Disclosure
          title={t('Conflicting Authorities')}
          badge={<Badge text={`${conflicts.length}`} className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800" />}
        >
          <ul className="space-y-1">
            {conflicts.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                {typeof c === 'string' ? c : (c as any).description || JSON.stringify(c)}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {/* ── Areas of Uncertainty ───────────────────────────── */}
      {uncertainty && uncertainty.length > 0 && (
        <Disclosure title={t('Areas of Uncertainty')}>
          {uncertainty.map((u, i) => (
            <div key={i} className="space-y-1 pl-3 border-l-2 border-amber-200 dark:border-amber-800">
              <p className="font-semibold text-[13px]">{u.area}</p>
              {u.current_status && <p className="text-[13px]">{u.current_status}</p>}
              {u.implications && <p className="text-[12px] text-gray-500 dark:text-gray-400 italic">{u.implications}</p>}
              {u.relevant_authorities && u.relevant_authorities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {u.relevant_authorities.map((a, j) => (
                    <CitationChip key={j} citation={a} onOpenCitation={onOpenCitation} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </Disclosure>
      )}

      {/* ── Gaps (only if substantive — no boilerplate) ───── */}
      {gaps && (
        (gaps.information_gaps && !['none', 'n/a', ''].includes(gaps.information_gaps.toLowerCase().trim())) ||
        (gaps.recommendations_for_deeper_research && !['none', 'n/a', ''].includes(gaps.recommendations_for_deeper_research.toLowerCase().trim()))
      ) && (
        <Disclosure title={t('Gaps & Recommendations')}>
          {gaps.information_gaps && !['none', 'n/a', ''].includes(gaps.information_gaps.toLowerCase().trim()) && (
            <p className="text-[13px]">{gaps.information_gaps}</p>
          )}
          {gaps.limitation_summary && !['none', 'n/a', ''].includes(gaps.limitation_summary.toLowerCase().trim()) && (
            <p className="text-[13px] text-gray-600 dark:text-gray-400">{gaps.limitation_summary}</p>
          )}
          {gaps.recommendations_for_deeper_research && !['none', 'n/a', ''].includes(gaps.recommendations_for_deeper_research.toLowerCase().trim()) && (
            <div className="p-2.5 bg-blue-50/60 dark:bg-blue-950/20 rounded-lg text-[12px] text-blue-700 dark:text-blue-300">
              <span className="font-semibold">{t('Recommendation')}:&nbsp;</span>
              {gaps.recommendations_for_deeper_research}
            </div>
          )}
        </Disclosure>
      )}

      {/* ── Practical Implications (only meaningful entries) ─ */}
      {pi && (() => {
        const boilerplate = ['n/a', 'none', 'not applicable', 'not applicable due to lack of specific legal authorities.', ''];
        const entries = Object.entries(pi).filter(
          ([, v]) => v && !boilerplate.includes(v.toLowerCase().trim())
        );
        if (entries.length === 0) return null;
        return (
          <Disclosure title={t('Practical Implications')}>
            <dl className="space-y-2">
              {entries.map(([key, val]) => (
                <div key={key}>
                  <dt className="text-[12px] font-semibold text-gray-500 dark:text-gray-400">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </dt>
                  <dd className="text-[13px]">{val}</dd>
                </div>
              ))}
            </dl>
          </Disclosure>
        );
      })()}
    </div>
  );
};

export default ResearchResultRenderer;
